import { useEffect, useRef } from 'react';
import { useStore } from '../store/useStore.js';
import { generateStars, getMoveHandle, getGroupHandle, readThemeVars, render, s2w, w2s, type Star } from './renderer.js';
import { uid } from '../utils/constants.js';
import type { GraphNode, GraphLink } from '@synapse/engine';

type StoreState = ReturnType<typeof useStore.getState>;

// Faquinha sutil (24x24, hotspot na ponta da lâmina). Embedded as data URL.
const KNIFE_CURSOR = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='%23ff8a8a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M4 20l4.5-4.5'/><path d='M8.5 15.5L19 5c.8 1.6.4 3.6-.9 4.9l-5.6 5.6c-.6.6-1.5.8-2.3.5z' fill='%23ff8a8a' fill-opacity='0.25'/></svg>") 4 20, crosshair`;

function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): boolean {
  const d = (x2 - x1) * (y4 - y3) - (y2 - y1) * (x4 - x3);
  if (d === 0) return false;
  const u = ((x3 - x1) * (y4 - y3) - (y3 - y1) * (x4 - x3)) / d;
  const v = ((x3 - x1) * (y2 - y1) - (y3 - y1) * (x2 - x1)) / d;
  return u >= 0 && u <= 1 && v >= 0 && v <= 1;
}

function computeKnifeCuts(
  worldPts: { x: number; y: number }[],
  links: GraphLink[],
  nodes: GraphNode[],
  cam: { x: number; y: number; zoom: number },
  width: number,
  height: number,
): Set<string> {
  const cuts = new Set<string>();
  if (worldPts.length < 2) return cuts;
  // Project knife polyline to screen space — bezier control point uses fixed
  // 22px screen offset, so consistent intersection requires screen coords.
  const knifeScreen = worldPts.map((p) => w2s(p.x, p.y, cam, width, height));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const offset = 22;
  for (const lk of links) {
    const a = byId.get(lk.source);
    const b = byId.get(lk.target);
    if (!a || !b) continue;
    const A = w2s(a.x, a.y, cam, width, height);
    const B = w2s(b.x, b.y, cam, width, height);
    const dx = B.sx - A.sx;
    const dy = B.sy - A.sy;
    const len = Math.hypot(dx, dy) || 1;
    const cpx = (A.sx + B.sx) / 2 + (-dy / len) * offset;
    const cpy = (A.sy + B.sy) / 2 + (dx / len) * offset;
    const N = 14;
    let prevX = A.sx, prevY = A.sy;
    let hit = false;
    for (let i = 1; i <= N && !hit; i++) {
      const t = i / N;
      const u = 1 - t;
      const sx = u * u * A.sx + 2 * u * t * cpx + t * t * B.sx;
      const sy = u * u * A.sy + 2 * u * t * cpy + t * t * B.sy;
      for (let j = 1; j < knifeScreen.length; j++) {
        const k1 = knifeScreen[j - 1]!;
        const k2 = knifeScreen[j]!;
        if (segmentsIntersect(prevX, prevY, sx, sy, k1.sx, k1.sy, k2.sx, k2.sy)) {
          cuts.add(lk.id);
          hit = true;
          break;
        }
      }
      prevX = sx; prevY = sy;
    }
  }
  return cuts;
}

function visibleState(state: StoreState): { nodes: GraphNode[]; links: GraphLink[] } {
  const fp = state.focusedParentId;
  const nodes = state.nodes.filter((n) =>
    fp === null ? n.parentId == null : n.parentId === fp,
  );
  const ids = new Set(nodes.map((n) => n.id));
  const links = state.links.filter((l) => ids.has(l.source) && ids.has(l.target));
  return { nodes, links };
}

interface Props {
  onWorldMove?: (worldX: number, worldY: number) => void;
}

export function GraphCanvas({ onWorldMove }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const starsRef = useRef<Star[]>([]);
  const dragRef = useRef<{ id: string | null; sx: number; sy: number; moved: boolean; ctrlKey?: boolean } | null>(null);
  const panRef = useRef<{ sx: number; sy: number; cx: number; cy: number } | null>(null);
  const linkStartRef = useRef<{ x: number; y: number } | null>(null);
  const hoveredLinkRef = useRef<string | null>(null);
  const selectedLinkRef = useRef<string | null>(null);
  // Knife mode: shift+drag on empty canvas to slice links.
  const knifeRef = useRef<{ points: { x: number; y: number }[] } | null>(null);
  // Space pressionado → arrastar canvas (pan). Clique simples vira seleção.
  const spaceDownRef = useRef(false);
  // Marquee de seleção (estilo Figma) — coords de tela.
  const marqueeRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  // Clique simples num nó: pulso + delay; segundo clique cancela e entra no nó.
  const clickTimerRef = useRef<number | null>(null);
  const pulseRef = useRef<{ id: string; start: number } | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    starsRef.current = generateStars(360);

    // Preload icon font so canvas ligatures work on first frame.
    document.fonts.load('24px "Material Symbols Outlined"', 'lightbulb').catch(() => {});

    function resize() {
      if (!canvas || !ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight - 54;
      canvas.width = Math.max(100, w * dpr);
      canvas.height = Math.max(100, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener('resize', resize);

    let last = performance.now();
    function loop(now: number) {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const state = useStore.getState();
      state.tick(dt);
      const { nodes: visNodes, links: visLinks } = visibleState(state);
      const themeVars = readThemeVars();
      render({
        ctx: ctx!,
        width: canvas!.clientWidth,
        height: canvas!.clientHeight,
        cam: state.cam,
        nodes: visNodes,
        links: visLinks,
        selectedId: state.selectedId,
        hoveredId: state.hoveredNode,
        multiSelected: state.multiSelected,
        time: state.time,
        stars: starsRef.current,
        themeVars,
        remoteCursors: state.remoteCursors,
        remoteSelections: state.remoteCursors.reduce((m, c) => {
          if (c.selectedNodeId) m.set(c.selectedNodeId, c.color);
          return m;
        }, new Map<string, string>()),
        linkingFrom: state.linkingFrom,
        linkDragPos: state.linkDragPos,
        searchQuery: state.searchQuery,
        snapToGrid: state.snapToGrid,
        hoveredLinkId: hoveredLinkRef.current,
        selectedLinkId: selectedLinkRef.current,
        knifePath: knifeRef.current ? knifeRef.current.points : undefined,
        noteEditingId: state.noteEditingId,
        noteCardBounds: state.noteCardBounds,
        marquee: marqueeRef.current,
        clickPulse: pulseRef.current,
      });
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const inEditor = ['INPUT', 'TEXTAREA'].includes((document.activeElement as HTMLElement | null)?.tagName ?? '') || (document.activeElement as HTMLElement | null)?.isContentEditable;
        if (inEditor) return;
        
        if (selectedLinkRef.current) {
          const state = useStore.getState();
          const lk = state.links.find(l => l.id === selectedLinkRef.current);
          if (lk) {
            e.preventDefault();
            state.removeLink(lk.source, lk.target);
            selectedLinkRef.current = null;
            state.snapshot();
          }
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);

    // Space pressionado = modo pan (cursor de mão). Ignora quando digitando.
    function isTyping(): boolean {
      const el = document.activeElement as HTMLElement | null;
      return ['INPUT', 'TEXTAREA'].includes(el?.tagName ?? '') || !!el?.isContentEditable;
    }
    function onSpaceDown(e: KeyboardEvent) {
      if (e.code !== 'Space' || isTyping()) return;
      e.preventDefault();
      if (!spaceDownRef.current) {
        spaceDownRef.current = true;
        if (ref.current && !panRef.current) ref.current.style.cursor = 'grab';
      }
    }
    function onSpaceUp(e: KeyboardEvent) {
      if (e.code !== 'Space') return;
      spaceDownRef.current = false;
      if (ref.current && !panRef.current) ref.current.style.cursor = 'crosshair';
    }
    window.addEventListener('keydown', onSpaceDown);
    window.addEventListener('keyup', onSpaceUp);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keydown', onSpaceDown);
      window.removeEventListener('keyup', onSpaceUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function pickNodeLabel(sx: number, sy: number, rect: DOMRect): string | null {
    const state = useStore.getState();
    const cam = state.cam;
    const w = rect.width;
    const h = rect.height;
    const mouseX = sx - rect.left;
    const mouseY = sy - rect.top;
    if (cam.zoom < 0.35) return null; // labels not rendered at low zoom
    for (const n of visibleState(state).nodes) {
      const scrX = (n.x + cam.x) * cam.zoom + w / 2;
      const scrY = (n.y + cam.y) * cam.zoom + h / 2;
      const baseR = (n.size || 22) * cam.zoom;
      const labelFont = Math.max(11, Math.min(64, (n.size || 22) * 0.62 * cam.zoom));
      const labelY = scrY + baseR + Math.max(12, labelFont * 0.9);
      const labelHalfW = Math.max(40, n.label.length * (labelFont * 0.32));
      if (Math.abs(mouseX - scrX) < labelHalfW && Math.abs(mouseY - labelY) < labelFont * 0.7) {
        return n.id;
      }
    }
    return null;
  }

  function pickMoveHandle(sx: number, sy: number, rect: DOMRect): string | null {
    const state = useStore.getState();
    const cam = state.cam;
    const w = rect.width;
    const h = rect.height;
    const mouseX = sx - rect.left;
    const mouseY = sy - rect.top;
    for (const n of visibleState(state).nodes) {
      const grip = getMoveHandle(n, cam, w, h);
      if (Math.abs(mouseX - grip.sx) < 13 && Math.abs(mouseY - grip.sy) < 9) return n.id;
    }
    return null;
  }

  function pickGroupHandle(sx: number, sy: number, rect: DOMRect): string | null {
    const state = useStore.getState();
    const cam = state.cam;
    const w = rect.width;
    const h = rect.height;
    const mouseX = sx - rect.left;
    const mouseY = sy - rect.top;
    for (const n of visibleState(state).nodes) {
      const grip = getGroupHandle(n, cam, w, h);
      if (Math.hypot(mouseX - grip.sx, mouseY - grip.sy) < 14) return n.id;
    }
    return null;
  }

  function pickLink(sx: number, sy: number, rect: DOMRect): string | null {
    const state = useStore.getState();
    const cam = state.cam;
    const w = rect.width;
    const h = rect.height;
    const mouseX = sx - rect.left;
    const mouseY = sy - rect.top;
    const { nodes: vn, links: vl } = visibleState(state);
    const byId = new Map(vn.map((n) => [n.id, n]));
    const THRESHOLD = 9;
    for (const lk of vl) {
      const a = byId.get(lk.source);
      const b = byId.get(lk.target);
      if (!a || !b) continue;
      const A = { sx: (a.x + cam.x) * cam.zoom + w / 2, sy: (a.y + cam.y) * cam.zoom + h / 2 };
      const B = { sx: (b.x + cam.x) * cam.zoom + w / 2, sy: (b.y + cam.y) * cam.zoom + h / 2 };
      const dx = B.sx - A.sx;
      const dy = B.sy - A.sy;
      const len = Math.hypot(dx, dy) || 1;
      const cpX = (A.sx + B.sx) / 2 + (-dy / len) * 22;
      const cpY = (A.sy + B.sy) / 2 + (dx / len) * 22;
      for (let t = 0; t <= 1; t += 1 / 14) {
        const px = (1 - t) * (1 - t) * A.sx + 2 * (1 - t) * t * cpX + t * t * B.sx;
        const py = (1 - t) * (1 - t) * A.sy + 2 * (1 - t) * t * cpY + t * t * B.sy;
        
        // Ignore clicks inside nodes
        const rA = (a.size || 22) * cam.zoom + 6;
        const rB = (b.size || 22) * cam.zoom + 6;
        if (Math.hypot(px - A.sx, py - A.sy) < rA) continue;
        if (Math.hypot(px - B.sx, py - B.sy) < rB) continue;

        if (Math.hypot(mouseX - px, mouseY - py) < THRESHOLD) return lk.id;
      }
    }
    return null;
  }

  function pickNode(sx: number, sy: number, rect: DOMRect): string | null {
    const state = useStore.getState();
    const cam = state.cam;
    const world = s2w(sx - rect.left, sy - rect.top, cam, rect.width, rect.height);
    let hit: string | null = null;
    let best = Infinity;
    for (const n of visibleState(state).nodes) {
      const d = Math.hypot(n.x - world.wx, n.y - world.wy);
      const r = (n.size || 22);
      if (d < r && d < best) { best = d; hit = n.id; }
    }
    return hit;
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (e.button === 2) return;
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    const state = useStore.getState();

    // Novo clique cancela qualquer abertura pendente de nota (duplo-clique).
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    // Space+clique (ou botão do meio) → pan do canvas, mesmo sobre nós.
    if (spaceDownRef.current || e.button === 1) {
      e.preventDefault();
      panRef.current = { sx: e.clientX, sy: e.clientY, cx: state.cam.x, cy: state.cam.y };
      if (ref.current) ref.current.style.cursor = 'grabbing';
      return;
    }

    // Click on a link → select it or delete it.
    const linkHit = pickLink(e.clientX, e.clientY, rect);
    if (linkHit) {
      if (selectedLinkRef.current === linkHit) {
        const lk = state.links.find((l) => l.id === linkHit);
        if (lk) {
          state.removeLink(lk.source, lk.target);
          selectedLinkRef.current = null;
          state.snapshot();
        }
      } else {
        selectedLinkRef.current = linkHit;
        state.setSelected(null);
      }
      return;
    }

    // Group icon → pull connected nodes closer.
    const groupId = pickGroupHandle(e.clientX, e.clientY, rect);
    if (groupId) {
      selectedLinkRef.current = null;
      const sourceNode = state.nodes.find((n) => n.id === groupId);
      if (sourceNode) {
        const connectedIds = state.links
          .filter((l) => l.source === groupId || l.target === groupId)
          .map((l) => (l.source === groupId ? l.target : l.source));
        
        connectedIds.forEach((connId) => {
          const connNode = state.nodes.find((n) => n.id === connId);
          if (connNode && !connNode.locked) {
            const dx = connNode.x - sourceNode.x;
            const dy = connNode.y - sourceNode.y;
            const dist = Math.hypot(dx, dy) || 1;
            const targetDist = ((sourceNode.size || 22) + (connNode.size || 22)) * 1.5 + 40;
            if (dist > targetDist) {
              const newX = sourceNode.x + (dx / dist) * targetDist;
              const newY = sourceNode.y + (dy / dist) * targetDist;
              state.patchNode(connId, { x: newX, y: newY, vx: 0, vy: 0 });
            }
          }
        });
        state.snapshot();
      }
      return;
    }

    // Grip icon → move node.
    const moveId = pickMoveHandle(e.clientX, e.clientY, rect);
    if (moveId) {
      selectedLinkRef.current = null;
      state.setSelected(moveId);
      dragRef.current = { id: moveId, sx: e.clientX, sy: e.clientY, moved: false };
      return;
    }

    const id = pickNode(e.clientX, e.clientY, rect);
    if (id) {
      selectedLinkRef.current = null;
      if (e.shiftKey) {
        // Shift+node: start link drag
        state.setSelected(id);
        linkStartRef.current = { x: e.clientX, y: e.clientY };
        state.setLinkingFrom(id);
        state.setLinkDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        return;
      }
      
      // Default left click on node: select it, close edit panel (so tooltip shows), and start drag move
      // Clique simples fora do grupo multi-selecionado limpa a multi-seleção;
      // clique em membro do grupo preserva (pra arrastar todos juntos).
      if (!(e.ctrlKey || e.metaKey) && state.multiSelected.size > 0 && !state.multiSelected.has(id)) {
        state.setMultiSelected(new Set());
      }
      state.setSelected(id);
      state.setEditPanelOpen(false);
      dragRef.current = { id, sx: e.clientX, sy: e.clientY, moved: false, ctrlKey: e.ctrlKey || e.metaKey };
      return;
    }

    // Empty canvas + Shift → knife mode (slice through links to cut them).
    if (e.shiftKey) {
      const world = s2w(e.clientX - rect.left, e.clientY - rect.top, state.cam, rect.width, rect.height);
      knifeRef.current = { points: [{ x: world.wx, y: world.wy }] };
      if (ref.current) ref.current.style.cursor = KNIFE_CURSOR;
      state.setSelected(null);
      state.setMultiSelected(new Set());
      selectedLinkRef.current = null;
      return;
    }

    // Canvas vazio → marquee de seleção (estilo Figma). Pan fica no Space+clique.
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    marqueeRef.current = { x1: mx, y1: my, x2: mx, y2: my };
    selectedLinkRef.current = null;
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    const state = useStore.getState();
    const wx = (e.clientX - rect.left - rect.width / 2) / state.cam.zoom - state.cam.x;
    const wy = (e.clientY - rect.top - rect.height / 2) / state.cam.zoom - state.cam.y;
    onWorldMove?.(wx, wy);

    // Hover — check node body OR grip handle so grip doesn't disappear.
    const hoveredNodeHit = pickNode(e.clientX, e.clientY, rect) ?? pickMoveHandle(e.clientX, e.clientY, rect) ?? pickGroupHandle(e.clientX, e.clientY, rect);
    if (hoveredNodeHit !== state.hoveredNode) state.setHovered(hoveredNodeHit);

    // Link hover.
    hoveredLinkRef.current = pickLink(e.clientX, e.clientY, rect);

    // Knife drag — append world points to slice path.
    if (knifeRef.current) {
      const world = s2w(e.clientX - rect.left, e.clientY - rect.top, state.cam, rect.width, rect.height);
      const pts = knifeRef.current.points;
      const last = pts[pts.length - 1]!;
      // Throttle: only append if moved at least 4 world units to keep array bounded.
      if (Math.hypot(world.wx - last.x, world.wy - last.y) > 4 / state.cam.zoom) {
        pts.push({ x: world.wx, y: world.wy });
      }
      return;
    }

    // Linking drag — only show line after moving past threshold.
    if (state.linkingFrom) {
      const moved = linkStartRef.current
        ? Math.hypot(e.clientX - linkStartRef.current.x, e.clientY - linkStartRef.current.y) > 8
        : true;
      if (moved) {
        state.setLinkDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
      return;
    }

    // Pan.
    if (panRef.current) {
      const p = panRef.current;
      const dx = (e.clientX - p.sx) / state.cam.zoom;
      const dy = (e.clientY - p.sy) / state.cam.zoom;
      state.setCamera({ x: p.cx + dx, y: p.cy + dy, zoom: state.cam.zoom });
      return;
    }

    // Marquee de seleção.
    if (marqueeRef.current) {
      marqueeRef.current.x2 = e.clientX - rect.left;
      marqueeRef.current.y2 = e.clientY - rect.top;
      return;
    }

    // Drag node.
    if (dragRef.current?.id) {
      const d = dragRef.current;
      const dxScreen = e.clientX - d.sx;
      const dyScreen = e.clientY - d.sy;
      if (Math.hypot(dxScreen, dyScreen) > 4) d.moved = true;
      const dxWorld = dxScreen / state.cam.zoom;
      const dyWorld = dyScreen / state.cam.zoom;
      d.sx = e.clientX;
      d.sy = e.clientY;
      const node = state.nodes.find((n) => n.id === d.id);
      if (node && !node.locked) {
        // Free movement during drag; snap only fires on mouseup so user can cross cells.
        // Se o nó arrastado faz parte da multi-seleção, o grupo inteiro acompanha.
        const groupIds = state.multiSelected.has(node.id) && state.multiSelected.size > 1
          ? [...state.multiSelected]
          : [node.id];
        for (const gid of groupIds) {
          const gn = state.nodes.find((n) => n.id === gid);
          if (!gn || gn.locked) continue;
          const nx = gn.x + dxWorld;
          const ny = gn.y + dyWorld;
          // homeX/homeY acompanham: sem isso a âncora da física puxa os
          // membros do grupo de volta pra posição antiga ao soltar.
          state.patchNode(gid, { x: nx, y: ny, vx: 0, vy: 0, homeX: nx, homeY: ny });
        }
        state.setDrag({ id: node.id, dx: dxScreen, dy: dyScreen, moved: true });
      }
    }
  }

  function handleMouseUp(e: React.MouseEvent<HTMLCanvasElement>) {
    const state = useStore.getState();
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();

    // Knife mode: compute link intersections, delete them, then clear.
    if (knifeRef.current) {
      const pts = knifeRef.current.points;
      knifeRef.current = null;
      if (ref.current) ref.current.style.cursor = 'crosshair';
      if (pts.length >= 2) {
        const cutIds = computeKnifeCuts(pts, state.links, state.nodes, state.cam, rect.width, rect.height);
        if (cutIds.size > 0) {
          for (const lk of [...state.links]) {
            if (cutIds.has(lk.id)) state.removeLink(lk.source, lk.target);
          }
          state.snapshot();
        }
      }
      return;
    }

    if (state.linkingFrom) {
      const reallyDragged = linkStartRef.current
        ? Math.hypot(e.clientX - linkStartRef.current.x, e.clientY - linkStartRef.current.y) > 8
        : false;

      if (reallyDragged) {
        const target = pickNode(e.clientX, e.clientY, rect);
        if (target && target !== state.linkingFrom) {
          state.createLink(state.linkingFrom, target, 'synapse');
        } else if (!target) {
          const world = s2w(e.clientX - rect.left, e.clientY - rect.top, state.cam, rect.width, rect.height);
          const sourceNode = state.nodes.find((n) => n.id === state.linkingFrom);
          const id = uid();
          let wx = world.wx;
          let wy = world.wy;
          if (state.snapToGrid) { const g = 40; wx = Math.round(wx / g) * g; wy = Math.round(wy / g) * g; }
          state.addNode({
            id, label: 'Nova Ideia', type: 'idea', icon: 'lightbulb',
            parentId: state.focusedParentId,
            color: sourceNode?.color ?? '#00f5ff',
            x: wx, y: wy, vx: 0, vy: 0, size: 22, locked: false, category: 'idea',
          });
          state.createLink(state.linkingFrom, id, 'synapse');
          setTimeout(() => state.setSelected(id), 10);
        }
      }
      // Either way, clear link state.
      state.setLinkingFrom(null);
      state.setLinkDragPos(null);
      linkStartRef.current = null;
    }

    if (dragRef.current) {
      if (dragRef.current.ctrlKey && !dragRef.current.moved) {
        // Ctrl+click without drag → toggle multi-select.
        const id = dragRef.current.id;
        if (id) {
          const m = new Set(state.multiSelected);
          if (m.has(id)) m.delete(id); else m.add(id);
          state.setMultiSelected(m);
        }
      } else if (dragRef.current.moved) {
        // Apply snap-to-grid on drop so user could freely move during drag.
        if (state.snapToGrid && dragRef.current.id) {
          const dragId = dragRef.current.id;
          const groupIds = state.multiSelected.has(dragId) && state.multiSelected.size > 1
            ? [...state.multiSelected]
            : [dragId];
          for (const gid of groupIds) {
            const node = state.nodes.find((n) => n.id === gid);
            if (node && !node.locked) {
              const g = 40;
              const nx = Math.round(node.x / g) * g;
              const ny = Math.round(node.y / g) * g;
              state.patchNode(gid, { x: nx, y: ny, vx: 0, vy: 0, homeX: nx, homeY: ny });
            }
          }
        }
        state.snapshot();
      } else if (dragRef.current.id) {
        // Clique simples no nó: pulso + delay. Se vier o 2º clique (duplo),
        // o timer é cancelado e entra no nó; senão abre a nota inline.
        const nodeId = dragRef.current.id;
        pulseRef.current = { id: nodeId, start: performance.now() };
        clickTimerRef.current = window.setTimeout(() => {
          clickTimerRef.current = null;
          useStore.getState().setNoteEditingId(nodeId);
        }, 300);
      }
    }

    // Marquee: seleciona nós dentro do retângulo; arrasto mínimo = clique vazio.
    if (marqueeRef.current) {
      const m = marqueeRef.current;
      marqueeRef.current = null;
      const dragged = Math.abs(m.x2 - m.x1) > 6 || Math.abs(m.y2 - m.y1) > 6;
      if (dragged) {
        const w1 = s2w(Math.min(m.x1, m.x2), Math.min(m.y1, m.y2), state.cam, rect.width, rect.height);
        const w2 = s2w(Math.max(m.x1, m.x2), Math.max(m.y1, m.y2), state.cam, rect.width, rect.height);
        const inside = visibleState(state).nodes.filter(
          (n) => n.x >= w1.wx && n.x <= w2.wx && n.y >= w1.wy && n.y <= w2.wy,
        );
        state.setMultiSelected(new Set(inside.map((n) => n.id)));
        state.setSelected(inside.length === 1 ? inside[0]!.id : null);
      } else {
        // Clique simples no vazio → limpa seleção (comportamento original).
        state.setSelected(null);
        state.setMultiSelected(new Set());
      }
    }

    dragRef.current = null;
    if (panRef.current && ref.current) {
      ref.current.style.cursor = spaceDownRef.current ? 'grab' : 'crosshair';
    }
    panRef.current = null;
    state.setDrag(null);
  }

  function handleDoubleClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();

    // Duplo clique cancela a abertura pendente da nota (clique simples).
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    // Double-click on label → inline edit.
    const labelId = pickNodeLabel(e.clientX, e.clientY, rect);
    if (labelId) {
      useStore.getState().setEditingLabelId(labelId);
      return;
    }

    const id = pickNode(e.clientX, e.clientY, rect);
    if (id) {
      useStore.getState().setNoteEditingId(null);
      useStore.getState().enterUniverse(id);
      return;
    }
    
    if (pickLink(e.clientX, e.clientY, rect)) {
      return;
    }

    // Double-click on empty canvas → create node at cursor position.
      const state = useStore.getState();
      const world = s2w(e.clientX - rect.left, e.clientY - rect.top, state.cam, rect.width, rect.height);
      let wx = world.wx;
      let wy = world.wy;
      if (state.snapToGrid) {
        const g = 40;
        wx = Math.round(wx / g) * g;
        wy = Math.round(wy / g) * g;
      }
      const newId = state.addNode({
        label: 'Nova Ideia', type: 'idea', category: 'idea',
        icon: 'lightbulb', x: wx, y: wy, size: 22,
      });
      setTimeout(() => state.setSelected(newId), 10);
  }

  // Zoom mínimo dinâmico: nunca deixa afastar muito além do conteúdo do Flow.
  // Evita o grafo virar um pontinho perdido com os elementos "sumindo".
  function minZoomFor(state: StoreState, w: number, h: number): number {
    const { nodes } = visibleState(state);
    if (nodes.length === 0) return 0.3;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of nodes) {
      if (n.x < minX) minX = n.x;
      if (n.x > maxX) maxX = n.x;
      if (n.y < minY) minY = n.y;
      if (n.y > maxY) maxY = n.y;
    }
    const bw = (maxX - minX) + 800;
    const bh = (maxY - minY) + 800;
    const fit = Math.min(w / bw, h / bh);
    // Permite afastar até ~70% do "fit all" — conteúdo sempre legível.
    return Math.max(0.12, Math.min(1, fit * 0.7));
  }

  function handleWheel(e: React.WheelEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    const state = useStore.getState();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    const minZ = minZoomFor(state, rect.width, rect.height);
    const newZoom = Math.max(minZ, Math.min(6, state.cam.zoom * factor));
    const world = s2w(mx, my, state.cam, rect.width, rect.height);
    // mouse-anchored: ponto sob cursor permanece.
    const newCamX = (mx - rect.width / 2) / newZoom - world.wx;
    const newCamY = (my - rect.height / 2) / newZoom - world.wy;
    state.setCamera({ x: newCamX, y: newCamY, zoom: newZoom });
    void w2s;
  }

  return (
    <canvas
      ref={ref}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      onWheel={handleWheel}
      onContextMenu={(e) => {
        e.preventDefault();
        const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
        const state = useStore.getState();
        const nodeId = pickNode(e.clientX, e.clientY, rect);
        if (nodeId) {
          state.setSelected(nodeId);
          state.setEditPanelOpen(true);
          return;
        }
        const linkId = pickLink(e.clientX, e.clientY, rect);
        const world = s2w(e.clientX - rect.left, e.clientY - rect.top, state.cam, rect.width, rect.height);
        state.setContextMenu({ x: e.clientX, y: e.clientY, worldX: world.wx, worldY: world.wy, nodeId, linkId });
      }}
      style={{
        position: 'fixed',
        top: 'var(--topbar-h)',
        left: 0, right: 0, bottom: 0,
        width: '100vw',
        height: 'calc(100vh - var(--topbar-h))',
        display: 'block',
        cursor: 'crosshair',
        background: 'var(--canvas-bg)',
      }}
    />
  );
}
